import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import WorkoutProgressTable from "../components/WorkoutProgressTable";
import ExerciseProgressTable from "../components/ExerciseProgressTable";
import WorkoutNoteModal from "../components/WorkoutNoteModal";
import EmptyStateMessage from "../components/EmptyStateMessage";
import SeoHead from "../components/SeoHead";
import "./Home.css";
import "../styles/Common.css";
import { useHomeData } from "../hooks/useHomeData";
import { formatWeightRange } from "../utils/formatWeightRange";
import { todayYMDLocal } from "../utils/dateUtils";
import { useI18n } from "../i18n/i18n";
import { getLocalizedExerciseName } from "../utils/exerciseName";
import { getAllExercisesCatalog } from "../services/customExerciseService";
import { buildLocalizedAlternates, getPrerenderPublicPaths } from "../seo/routePolicy";
import { muscleGroups } from "../data/muscleGroups";
import { useSnapshotRenderMode } from "../utils/prerender";
import { buildProgressDates } from "./homeDateWindow";
import { calculateExerciseVolume, formatVolumeLoad } from "../utils/volumeLoad";

function Home() {
    const { t, locale, localizePath } = useI18n();
    const { dates, loggedExercises, exerciseLog, workoutNotes, updateWorkoutNote } = useHomeData();
    const [noteModal, setNoteModal] = useState({
        isOpen: false,
        date: "",
        initialNote: "",
    });

    const handleOpenNoteModal = ({ date, note }) => {
        setNoteModal({
            isOpen: true,
            date,
            initialNote: note !== undefined ? note : (workoutNotes?.[date] || ""),
        });
    };

    const handleCloseNoteModal = () => {
        setNoteModal((prev) => ({ ...prev, isOpen: false }));
    };

    const handleSaveNote = async (date, noteText) => {
        if (updateWorkoutNote) {
            await updateWorkoutNote(date, noteText);
        }
        handleCloseNoteModal();
    };
    const snapshotMode = useSnapshotRenderMode();
    const [displayToday, setDisplayToday] = useState(() => {
        if (typeof document !== "undefined") {
            const saved = document.getElementById("home-snapshot-today")?.getAttribute("data-today");
            if (saved) {
                return saved;
            }
        }

        return todayYMDLocal();
    });
    const [progressView, setProgressView] = useState("muscleGroups");
    const [showIndirect, setShowIndirect] = useState(true);

    useEffect(() => {
        setDisplayToday(todayYMDLocal());
    }, []);

    useEffect(() => {
        try {
            const saved = localStorage.getItem("home_showIndirect");
            if (saved !== null) {
                setShowIndirect(saved === "true");
            }
        } catch (_) {
            // ignore storage errors
        }
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem("home_showIndirect", String(showIndirect));
        } catch (_) {
            // ignore storage errors
        }
    }, [showIndirect]);

    const exerciseMap = useMemo(
        () => Object.fromEntries(getAllExercisesCatalog().map((exercise) => [exercise.id, exercise])),
        []
    );

    const hasAnyLoggedExercises = useMemo(() => {
        return Object.values(exerciseLog || {}).some((day) => day && Object.keys(day).length > 0);
    }, [exerciseLog]);

    const todayVolumeLoad = useMemo(() => {
        return (loggedExercises || []).reduce(
            (sum, item) => sum + calculateExerciseVolume(item.sets),
            0
        );
    }, [loggedExercises]);

    const todayWorkoutNote = workoutNotes?.[displayToday] || "";
    const hasTodayWorkoutNote = Boolean(todayWorkoutNote && todayWorkoutNote.trim().length > 0);
    const visibleDates = useMemo(() => {
        return buildProgressDates({
            dates,
            displayToday,
            exerciseLog,
            workoutNotes,
            minimumDates: 5,
            maxDates: 8,
        });
    }, [dates, displayToday, exerciseLog, workoutNotes]);
    const homeJsonLd = useMemo(
        () => {
            const siteUrl = "https://fit-log.com";
            const dashboardUrl = `${siteUrl}${localizePath("/")}`;

            const breadcrumbList = {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    {
                        "@type": "ListItem",
                        "position": 1,
                        "name": t("breadcrumbs.dashboard"),
                        "item": dashboardUrl
                    }
                ]
            };

            const appSchema = {
                "@type": "SoftwareApplication",
                "name": "Fit-Log",
                "applicationCategory": "HealthApplication",
                "operatingSystem": "Web",
                "description": t("home.jsonLdDescription"),
                "url": dashboardUrl,
            };

            return {
                "@context": "https://schema.org",
                "@graph": [breadcrumbList, appSchema]
            };
        },
        [t, localizePath]
    );
    const prerenderPaths = useMemo(
        () => getPrerenderPublicPaths(muscleGroups.map((group) => group.id)),
        []
    );

    return (
        <div className="homePage pageContainer">
            <SeoHead
                title={`Fit-Log - ${t("home.title")}`}
                description={t("home.jsonLdDescription")}
                canonicalPath={localizePath("/")}
                jsonLd={homeJsonLd}
                alternateLocales={buildLocalizedAlternates("/")}
            />
            <Breadcrumbs current={t("breadcrumbs.dashboard")} />
            <header className="homeHero">
                <h1 className="sectionHeading">{t("home.title")}</h1>
                <p className="homeSubtitle">{t("home.subtitle")}</p>
            </header>
            <span id="home-snapshot-today" data-today={displayToday} hidden aria-hidden="true" />
            {snapshotMode && (
                <nav aria-hidden="true" style={{ display: "none" }}>
                    {prerenderPaths.map((path) => (
                        <a key={path} href={path}>{path}</a>
                    ))}
                </nav>
            )}

            <div className="section">
                <div className="sectionHeadingRow">
                    <h2 className="sectionHeading">{t("home.loggedToday")}</h2>
                    {loggedExercises.length > 0 && (
                        <Link
                            to={localizePath("/SelectExercise")}
                            className="appButton homeActionButton"
                        >
                            <span>{t("home.logExercise")}</span>
                            <span className="homeActionButtonSymbol" aria-hidden="true">+</span>
                        </Link>
                    )}
                </div>
                {loggedExercises.length === 0 ? (
                    <EmptyStateMessage />
                ) : (
                    <table className="compactExerciseTable">
                        <thead>
                        <tr>
                            <th>{t("home.exercise")}</th>
                            <th>{t("home.setsCount")}</th>
                            <th>{t("home.weight")}</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr className="volumeLoadRow">
                            <td>
                                <span className="volumeLoadLabel">{t("home.volumeLoad")}</span>
                            </td>
                            <td>
                                {loggedExercises.reduce((sum, item) => sum + (Array.isArray(item.sets) ? item.sets.length : 0), 0)}
                            </td>
                            <td className="volumeLoadValue">
                                {formatVolumeLoad(todayVolumeLoad, locale)}
                            </td>
                        </tr>
                        {loggedExercises.map(({ exerciseId, sets }) => {
                            const exercise = exerciseMap[exerciseId];
                            const exerciseName = exercise
                                ? getLocalizedExerciseName(exercise, t)
                                : t("home.unknownExercise", { id: exerciseId });

                            return (
                                <tr key={exerciseId}>
                                    <td>
                                        {snapshotMode ? (
                                            <span className="linkButton appLinkButton">{exerciseName}</span>
                                        ) : (
                                            <Link
                                                to={localizePath(`/LogExercise/${exerciseId}/${displayToday}`)}
                                                className="linkButton appLinkButton"
                                            >
                                                {exerciseName}
                                            </Link>
                                        )}
                                    </td>
                                    <td>{sets.length}</td>
                                    <td>{formatWeightRange(sets, { naLabel: t("common.na") })}</td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                )}

                <div className="todayNoteCard" data-testid="today-workout-note">
                    <div className="todayNoteHeader">
                        <div className="todayNoteTitleBlock">
                            <span className="todayNoteIcon" aria-hidden="true">📝</span>
                            <span className="todayNoteLabel">{t("home.todayWorkoutNote")}</span>
                        </div>
                        <button
                            type="button"
                            className={`todayNoteActionButton ${hasTodayWorkoutNote ? "todayNoteActionButton--hasNote" : "todayNoteActionButton--empty"}`}
                            onClick={() => handleOpenNoteModal({ date: displayToday, note: todayWorkoutNote })}
                            aria-label={hasTodayWorkoutNote ? t("home.editNote") : t("home.addNote")}
                        >
                            {hasTodayWorkoutNote ? (
                                <>
                                    <span className="todayNoteActionIcon" aria-hidden="true">✏️</span>
                                    <span>{t("home.editNote")}</span>
                                </>
                            ) : (
                                <>
                                    <span className="todayNoteActionPlus" aria-hidden="true">+</span>
                                    <span>{t("home.addNote")}</span>
                                </>
                            )}
                        </button>
                    </div>
                    {hasTodayWorkoutNote ? (
                        <div
                            className="todayNoteBody todayNoteBody--filled"
                            onClick={() => handleOpenNoteModal({ date: displayToday, note: todayWorkoutNote })}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleOpenNoteModal({ date: displayToday, note: todayWorkoutNote });
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            title={t("home.editNote")}
                        >
                            <p className="todayNoteText">{todayWorkoutNote}</p>
                        </div>
                    ) : (
                        <div
                            className="todayNoteBody todayNoteBody--empty"
                            onClick={() => handleOpenNoteModal({ date: displayToday, note: todayWorkoutNote })}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleOpenNoteModal({ date: displayToday, note: todayWorkoutNote });
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            title={t("home.addNote")}
                        >
                            <span className="todayNotePlaceholderText">{t("home.clickToAddNote")}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="section">
                <div className="sectionHeadingRow">
                    <h2 className="sectionHeading">{t("home.progressTitle")}</h2>
                </div>
                <div className="progressControls">
                    <div
                        className="progressTabs"
                        role="tablist"
                        aria-label={t("home.progressTabsLabel")}
                    >
                        <button
                            type="button"
                            role="tab"
                            aria-selected={progressView === "muscleGroups"}
                            className={`progressTab${progressView === "muscleGroups" ? " progressTab--active" : ""}`}
                            onClick={() => setProgressView("muscleGroups")}
                        >
                            {t("home.progressByMuscleGroups")}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={progressView === "exercisesByDate"}
                            className={`progressTab${progressView === "exercisesByDate" ? " progressTab--active" : ""}`}
                            onClick={() => setProgressView("exercisesByDate")}
                        >
                            {t("home.progressByDate")}
                        </button>
                    </div>
                    {hasAnyLoggedExercises && progressView === "muscleGroups" && (
                        <button
                            type="button"
                            className={`indirectToggleButton${showIndirect ? " indirectToggleButton--active" : ""}`}
                            onClick={() => setShowIndirect((prev) => !prev)}
                            aria-pressed={showIndirect}
                            aria-label={t("home.toggleIndirectAria")}
                            title={t("home.toggleIndirectAria")}
                        >
                            <input
                                type="checkbox"
                                checked={showIndirect}
                                readOnly
                                aria-hidden="true"
                            />
                            <span>{t("home.toggleIndirect")}</span>
                        </button>
                    )}
                </div>
                <div className="tableContainer">
                    {progressView === "muscleGroups" ? (
                        <WorkoutProgressTable
                            dates={visibleDates}
                            exerciseLog={exerciseLog}
                            workoutNotes={workoutNotes}
                            onOpenNoteModal={handleOpenNoteModal}
                            showIndirect={showIndirect}
                        />
                    ) : (
                        <ExerciseProgressTable
                            dates={visibleDates}
                            exerciseLog={exerciseLog}
                            workoutNotes={workoutNotes}
                            onOpenNoteModal={handleOpenNoteModal}
                        />
                    )}
                </div>
            </div>

            <WorkoutNoteModal
                isOpen={noteModal.isOpen}
                date={noteModal.date}
                initialNote={noteModal.initialNote}
                onSave={handleSaveNote}
                onClose={handleCloseNoteModal}
            />

            { /* Export/Import buttons removed as requested */ }
        </div>
    );
}

export default Home;

